import React from "react"

import type {DOMConversionMap, DOMConversionOutput, DOMExportOutput, LexicalNode, Spread} from 'lexical'

import {
    type EditorConfig,
    type NodeKey,
    $applyNodeReplacement,
    DecoratorNode, SerializedLexicalNode,
} from 'lexical'

import {MentionOption} from "../option"
import MentionComponent from "./component"
import {EntityType} from "../../../../../gd/const/item"
import {renderToString} from "react-dom/server"

export type SerializedMentionNode = Spread<{
        mentionData: MentionOption
    },
    SerializedLexicalNode>

function convertMentionElement(
    domNode: HTMLElement,
): DOMConversionOutput | null {
    const mentionData = domNode.getAttribute('mention-data')

    if (mentionData !== null) {
        const node = $createMentionNode(JSON.parse(mentionData))
        return {
            node,
        }
    }

    return null
}

export class MentionNode extends DecoratorNode<JSX.Element> {
    __mentionData   : any

    static getType(): string {
        return 'mention'
    }

    static clone(node: MentionNode): MentionNode {
        return new MentionNode(node.__mentionData, node.__key)
    }

    static importJSON(serializedNode: SerializedMentionNode): MentionNode {
        return $createMentionNode(serializedNode.mentionData)
    }

    constructor(mentionData: MentionOption, key?: NodeKey) {
        super(key)
        this.__mentionData  = mentionData
    }

    get mentionData():any {
        return this.__mentionData
    }

    exportJSON(): SerializedMentionNode {
        return {
            mentionData     : this.__mentionData,
            type            : 'mention',
            version         : 1,
        }
    }

    createDOM(config: EditorConfig): HTMLElement {
        const dom       = document.createElement('span')
        const theme     = config.theme
        const className = theme.mention
        if (className !== undefined) {
            dom.className = className
        }
        return dom;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span')
        element.setAttribute('data-lexical-mention', 'true')
        element.setAttribute('mention-data', JSON.stringify(this.__mentionData))
        element.innerHTML = renderToString(<MentionComponent mention={this.__mentionData}/>)
        return {element}
    }

    updateDOM(): false {
        return false
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-lexical-mention')) {
                    return null
                }
                return {
                    conversion: convertMentionElement,
                    priority: 1,
                }
            },
        };
    }

    getTextContent(): string {
        switch (this.__mentionData.type) {
            case EntityType.TASK       : return `${this.__mentionData.name}`
            case EntityType.DOCUMENT   : return `${this.__mentionData.name}`
            case EntityType.USER       : return `@${this.__mentionData.name}`
            case EntityType.PROJECT    : return `${this.__mentionData.name}`
        }

        return this.__mentionData.name;
    }

    decorate(): JSX.Element {
        return <MentionComponent mention={this.__mentionData}/>
    }
}

export function $createMentionNode(data: any): MentionNode {
    return $applyNodeReplacement(new MentionNode(data))
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
    return node instanceof MentionNode
}
